// Layer 3 (Transformation): pure projection for the Quality › Impurities
// smelter-penalty panel (paul-fmea.A15 / #280). Reads `CONCENTRATE_SPECS`
// (the `ConcentrateSpec` entity) plus the `Reading`s it joins to — no
// literal threshold table. See `concentrate-spec.ts` for why the contract
// limit and the typical-trigger band stay two distinct fields.

import type { ConcentrateSpec, PenaltyElementRow } from '@/versions/v1/schema/concentrate-spec';
import { arsenicContractLimit, CONCENTRATE_SPECS } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';

export type PenaltyRowTone = 'ok' | 'warn' | 'bad' | 'neutral';

export type PenaltyRowView = {
  element: string;
  currentText?: string;
  contractLimitText?: string;
  /** Assay ÷ contract limit (headroom bar on Quality › Impurities). */
  limitRatio?: number;
  tone: PenaltyRowTone;
  statusLabel: string;
  triggerBandText?: string;
  triggerProvenance?: string;
  contractProvenance?: string;
  measurementProvenance?: string;
};

export type PenaltyConcentrateGroup = {
  id: string;
  label: string;
  rows: readonly PenaltyRowView[];
};

export type PenaltyPanelModel = {
  groups: readonly PenaltyConcentrateGroup[];
};

const TRENDING_READING_IDS = new Set(['rd-spec-pb-as']);

function fixedSuffix(value: number, unit: string, decimals: number): string {
  const text = value.toFixed(decimals);
  return unit === '%' ? `${text}%` : `${text} ${unit}`;
}

function formatBand(lo: number | undefined, hi: number | undefined, unit: string, decimals: number): string {
  if (lo !== undefined && hi !== undefined) return `${lo.toFixed(decimals)}–${fixedSuffix(hi, unit, decimals)}`;
  if (hi !== undefined) return fixedSuffix(hi, unit, decimals);
  if (lo !== undefined) return `> ${fixedSuffix(lo, unit, decimals)}`;
  return '—';
}

function penaltyRowView(row: PenaltyElementRow): PenaltyRowView {
  const triggerBandText = row.typicalTrigger
    ? formatBand(row.typicalTrigger.lo, row.typicalTrigger.hi, row.typicalTrigger.unit, row.typicalTrigger.decimals)
    : undefined;
  const triggerProvenance = row.typicalTrigger?.provenance;

  const { contractLimit } = row;
  if (!contractLimit) {
    return {
      element: row.element,
      tone: 'neutral',
      statusLabel: '—',
    };
  }
  const reading = readingByIdLookup(contractLimit.readingId);
  const value = reading?.value;
  const decimals = reading?.decimals ?? 2;
  const nonConforming = value !== undefined && value > contractLimit.hi;
  const trending = TRENDING_READING_IDS.has(contractLimit.readingId);
  const tone: PenaltyRowTone = nonConforming ? 'bad' : trending ? 'warn' : 'ok';
  const statusLabel = nonConforming ? 'OVER LIMIT' : trending ? 'WATCH' : 'OK';

  return {
    element: row.element,
    currentText: value !== undefined ? fixedSuffix(value, contractLimit.unit, decimals) : undefined,
    contractLimitText: fixedSuffix(contractLimit.hi, contractLimit.unit, decimals),
    limitRatio: value !== undefined && contractLimit.hi > 0 ? value / contractLimit.hi : undefined,
    tone,
    statusLabel,
    triggerBandText,
    triggerProvenance,
    contractProvenance: contractLimit.provenance,
    measurementProvenance: row.measurement?.provenance,
  };
}

function groupForSpec(spec: ConcentrateSpec): PenaltyConcentrateGroup {
  return {
    id: spec.id,
    label: spec.label,
    rows: spec.penaltyElements.map(penaltyRowView),
  };
}

export function concentratePenaltyPanelModel(): PenaltyPanelModel {
  const groups = CONCENTRATE_SPECS.map(groupForSpec);
  return { groups };
}

export { arsenicContractLimit };
